// Rundown cookie consent — GDPR/ePrivacy (global opt-in).
//
// The banner shows to every visitor; the "analytics" category is OFF until
// they Accept. PostHog is gated by marking its <script> in index.html as
// type="text/plain" data-category="analytics" — CookieConsent rewrites it to
// an executable script only once analytics consent is granted (and re-runs it
// automatically on return visits that already consented). The analytics.ts
// wrapper already no-ops while window.posthog is absent.
//
// Re-open preferences from anywhere via openCookiePreferences(), or any element
// with a data-cc="show-preferencesModal" attribute.
import * as CookieConsent from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'

export function openCookiePreferences(): void {
  CookieConsent.showPreferences()
}

export function initCookieConsent(): void {
  // Mirror the app's current theme.
  if (document.documentElement.classList.contains('dark')) {
    document.documentElement.classList.add('cc--darkmode')
  }

  void CookieConsent.run({
    mode: 'opt-in',
    disablePageInteraction: false,
    guiOptions: {
      consentModal: { layout: 'box', position: 'bottom right', equalWeightButtons: true, flipButtons: false },
      preferencesModal: { layout: 'box', position: 'right', equalWeightButtons: true, flipButtons: false },
    },

    categories: {
      necessary: {
        enabled: true,
        readOnly: true,
      },
      analytics: {
        autoClear: {
          cookies: [{ name: /^ph_/ }, { name: /^__ph/ }],
        },
        services: {
          posthog: {
            label: 'PostHog — privacy-friendly product analytics (EU-hosted)',
          },
        },
      },
    },

    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We use cookies',
            description:
              'We use a strictly-necessary set of cookies to make Rundown work, and — only with your permission — privacy-friendly analytics to understand how the app is used so we can improve it. You can accept, reject, or choose what to allow.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Manage preferences',
            footer:
              '<a href="https://getrundown.xyz/privacy.html">Privacy Policy</a> · <a href="https://getrundown.xyz/terms.html">Terms of Service</a>',
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'Your privacy choices',
                description:
                  'Rundown only sets non-essential cookies with your consent. You can change these choices at any time from the "Cookie preferences" link in the footer.',
              },
              {
                title: 'Strictly necessary',
                description:
                  'Required for the app to function — sign-in/session, security, payments, and remembering your cookie choices. These cannot be switched off.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics',
                description:
                  'Privacy-friendly, EU-hosted product analytics (PostHog) that help us understand usage and improve Rundown. No data is sold or shared for advertising.',
                linkedCategory: 'analytics',
                cookieTable: {
                  headers: { name: 'Cookie', domain: 'Domain', desc: 'Description' },
                  body: [
                    {
                      name: 'ph_*_posthog',
                      domain: location.hostname,
                      desc: 'Stores an anonymous device id and session state for product analytics.',
                    },
                  ],
                },
              },
            ],
          },
        },
      },
    },
  })
}
